import React, { useState, useEffect } from 'react';
import { X, Send, User, MessageSquare } from 'lucide-react';
import { User as AppUser } from '../models/User';
import { getUserById, getDisplayName } from '../data/userDatabase.ts';
import ProfilePicture from './ProfilePicture';
import { sendMessage } from '../data/messageDatabase';
import { Button } from './ui/button';

interface ContactUserModalProps {
  recipientId: string;
  source: 'Timeline' | 'Meetings' | 'Marketplace' | 'Financial Reports';
  isOpen: boolean;
  onClose: () => void;
  onSend: (recipientId: string, content: string) => void;
  onSuccess: (recipientId: string) => void;
}

const ContactUserModal: React.FC<ContactUserModalProps> = ({
  recipientId,
  source,
  isOpen,
  onClose,
  onSend,
  onSuccess
}) => {
  const [recipient, setRecipient] = useState<AppUser | null>(null);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load recipient user data
  useEffect(() => {
    const loadRecipient = async () => {
      if (isOpen && recipientId) {
        setIsLoading(true);
        try {
          const user = await getUserById(recipientId);
          setRecipient(user || null);
        } catch (error) {
          console.error('Failed to load recipient:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadRecipient();
  }, [isOpen, recipientId]);

  // Reset message and error when modal opens
  useEffect(() => {
    if (isOpen) {
      setMessage('');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || !recipient) return;

    setIsSending(true);
    setError(null);
    
    try {
      await onSend(recipientId, message);
      setMessage('');
      onClose();
      // Small delay to ensure the modal closes before showing success popup
      setTimeout(() => {
        onSuccess(recipientId);
      }, 100);
    } catch (error) {
      console.error('Failed to send message:', error);
      setError('Failed to send message. Please try again.');
      // Don't close modal on error, let user try again
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              {recipient ? (
                <ProfilePicture
                  userId={recipient.id}
                  fullName={getDisplayName(recipient)}
                  profilePictureUrl={recipient.profilePictureUrl}
                  profilePicture={recipient.profilePicture}
                  size="sm"
                />
              ) : (
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
              )}
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Contact {recipient ? getDisplayName(recipient) : 'User'}
              </h3>
              {isLoading && (
                <p className="text-sm text-gray-500">Loading user...</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSending}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
              Message
            </label>
            <textarea
              id="message"
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-none"
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              disabled={isSending || isLoading}
            />
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 rounded-md border border-red-200">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Source indicator */}
          <div className="mb-4 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Question from {source}</strong>
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={isSending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="outline"
              size="sm"
              disabled={!message.trim() || isSending || isLoading}
            >
              {isSending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactUserModal; 